import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Typography,
  typographyVariants,
} from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

const Chip = ({ value, onRemove }: { value: string; onRemove: () => void }) => (
  <Stack
    direction="row"
    gap="xs"
    align="center"
    className={cn(
      typographyVariants({ variant: "bodyW2" }),
      "rounded-full border border-stroke-tertiary bg-surface-secondary py-1 pl-3 pr-1",
    )}
  >
    <span>{value}</span>
    {/* 24px target (WCAG 2.5.8) with a name, not an anonymous ✕. */}
    <button
      type="button"
      aria-label={`Remove ${value}`}
      onClick={onRemove}
      className="flex h-6 w-6 items-center justify-center rounded-full text-secondary-text transition-colors hover:bg-gray-10 hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
    </button>
  </Stack>
);

/**
 * Removable chips with a quiet "+ Add …" under them, where the new chip will
 * appear. Enter adds; Escape (or clicking away while empty) closes the input.
 * Removing saves at once, so it offers Undo in a toast.
 */
export const EditableChips = ({
  values,
  onAdd,
  onRemove,
  noun,
  placeholder,
}: {
  values: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  /** Singular, lower case: "address", "domain". */
  noun: string;
  placeholder: string;
}) => {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);

  const add = () => {
    const value = draft.trim();
    if (value) onAdd(value);
    setDraft("");
  };

  const remove = (value: string) => {
    onRemove(value);
    toast(`Removed ${value}`, {
      action: {
        label: "Undo",
        onClick: () => onAdd(value),
      },
    });
  };

  return (
    <Stack gap="sm" fullWidth>
      {values.length > 0 ? (
        <Stack direction="row" gap="sm" align="center" className="flex-wrap">
          {values.map((v) => (
            <Chip key={v} value={v} onRemove={() => remove(v)} />
          ))}
        </Stack>
      ) : (
        !adding && <Typography textColor="tertiary">None yet.</Typography>
      )}

      {adding ? (
        <Input
          autoFocus
          fullWidth
          aria-label={`New ${noun}`}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          // Clicking away from an empty input closes it; a typed value stays.
          onBlur={() => !draft.trim() && setAdding(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
            if (e.key === "Escape") {
              setDraft("");
              setAdding(false);
            }
          }}
          endAdornment={
            <button
              type="button"
              aria-label={`Add ${draft.trim() || noun}`}
              onClick={add}
              disabled={!draft.trim()}
              className="rounded-sm text-tertiary-text transition-colors hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
            >
              <PlusIcon className="h-5 w-5" aria-hidden />
            </button>
          }
        />
      ) : (
        <Button
          variant="text"
          size="xsPill"
          // Green = interactive (AA 4.8:1), same as the Invoices job links;
          // stays green on hover and underlines, instead of the text variant's
          // hover-to-black.
          className="self-start px-0 text-interactive-text hover:text-interactive-text hover:underline"
          leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
          onClick={() => setAdding(true)}
        >
          Add {noun}
        </Button>
      )}
    </Stack>
  );
};
